/* eslint-disable react-hooks/exhaustive-deps */
import { useAnalytics } from '@ecosystem/newron-client-lib';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler/build/OutsideClickHandler';

import { PageDetailsSelector, Product, SlidesByType } from '../../common';
import { useUpsellLocalStorage, useUpsellSlides } from '../../hooks';
import { UpsellContext, initialState, reducer, actions as stateActions } from '../../state';
import { waitForElement } from '../../utils';
import { getMessageCode } from '../consumer-slides-message/utils/getMessageCode';
import { Notification } from '../notification';
import { getMessageCode as getCSRLSMessageCode } from '../slides/consumer-stories-retail-lost-shoppers-slide/utils/getMessageCode';
import { UpsellModal } from '../upsell-modal';

import './UpsellGuide.css';

type UpsellGuideType = {
  products: Product[];
};

const SECOND = 1000;
const EmptySelector = { show: '', wait: '' };

export function UpsellGuide({ products = [Product.Market] }: UpsellGuideType): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selector, setSelector] = useState<PageDetailsSelector>(EmptySelector);
  const { showUpsell, selectors, slides, pageName, market, UpsellMessage, slidesType, bannerCount } =
    useUpsellSlides(products);
  const { track } = useAnalytics();
  const { updateLocalState } = useUpsellLocalStorage(products);
  const [showUpsellOverlay, setShowUpsellOverlay] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const bannerIsOpen = useRef<boolean>(false);
  const showUpsellRef = useRef<boolean>(showUpsell);

  const contextValue = useMemo(() => ({ state, dispatch }), [state]);

  const onCloseMessage = () => {
    setShowUpsellOverlay(false);
    updateLocalState(market, true);
    setShowBanner(false);
  };

  useEffect(() => {
    bannerIsOpen.current = false;
  }, [selectors]);

  useEffect(() => {
    if (showUpsellOverlay && !bannerIsOpen.current) {
      if (slidesType === SlidesByType.CONSUMER_LOST_SHOPPER) {
        track('upsell-banner-shown', {
          pageName: selector.name ?? pageName,
          bannerName: getMessageCode(bannerCount ?? 0),
        });
        setShowBanner(true);
      }
      if (slidesType === SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER) {
        track('upsell-banner-shown', {
          pageName: selector.name ?? pageName,
          bannerName: getCSRLSMessageCode(bannerCount ?? 0),
        });
        setShowBanner(true);
      }
      bannerIsOpen.current = true;
    }
  }, [track, showUpsellOverlay, showBanner, slidesType, selector.name, pageName, bannerCount]);
  useEffect(() => {
    showUpsellRef.current = showUpsell;
  }, [showUpsell]);

  useEffect(() => {
    dispatch(stateActions.setMarket(market));
  }, [market]);

  useEffect(() => {
    dispatch(stateActions.setPageName(pageName));
  }, [pageName]);

  useEffect(() => {
    dispatch(stateActions.setBannerNumber(bannerCount));
  }, [bannerCount]);

  useEffect(() => {
    dispatch(stateActions.setShowSingleSlide(null));
  }, [slidesType]);

  useEffect(() => {
    let timeoutId;
    const show = async () => {
      const existingSelector = await Promise.any(
        selectors.map(
          s =>
            // eslint-disable-next-line no-async-promise-executor
            new Promise<PageDetailsSelector>(async resolve => {
              await waitForElement(s.wait);
              resolve(s);
            })
        )
      ).catch(() => {
        /* do nothing */
      });

      if (existingSelector && showUpsellRef.current) {
        timeoutId = setTimeout(() => {
          setSelector(existingSelector);
          setShowUpsellOverlay(true);
        }, SECOND);
      }
    };

    if (showUpsell) {
      show();
    }

    return () => {
      timeoutId && clearTimeout(timeoutId);
      setShowUpsellOverlay(false);
      setShowBanner(false);
      dispatch(stateActions.setIsModalOpen(false));
    };
  }, [selectors, showUpsell, market]);

  const onOutsideClick = e => {
    const isBannerContainer = document.getElementsByClassName('Root-mfe-upsell-tooltip')[0]?.contains(e.target);
    const isContextBarContainer = document.querySelectorAll('*[class^="ContextBar_ContextBar"]')[0];
    if (showUpsellOverlay && !state.isModalOpen && !isBannerContainer && !isContextBarContainer) {
      setShowBanner(false);
    }
  };

  const onUpsellMessageClose = useCallback(() => {
    if (slidesType === SlidesByType.CONSUMER_LOST_SHOPPER) {
      track('dismiss-banner', {
        pageName: selector.name ?? pageName,
        bannerName: getMessageCode(bannerCount),
      });
    }
    if (slidesType === SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER) {
      track('dismiss-banner', {
        pageName: selector.name ?? pageName,
        bannerName: getCSRLSMessageCode(bannerCount),
      });
    }
    onCloseMessage();
  }, [slidesType]);

  const onShowMore = useCallback(() => {
    if (slidesType === SlidesByType.CONSUMER_LOST_SHOPPER) {
      track('upsell-pop-up-shown', {
        pageName: selector.name ?? pageName,
        bannerName: getMessageCode(bannerCount),
      });
    }
    if (slidesType === SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER) {
      track('upsell-pop-up-shown', {
        pageName: selector.name ?? pageName,
        bannerName: getCSRLSMessageCode(bannerCount),
      });
    }
    onCloseMessage();
    dispatch(stateActions.setIsModalOpen(true));
    updateLocalState(market, true, true);
  }, [slidesType]);

  const onCloseTrack = useCallback(
    (slideNumber: number) => {
      if (slidesType === SlidesByType.CONSUMER_LOST_SHOPPER) {
        track('dismiss-upsell-pop-up', {
          pageName: selector.name ?? pageName,
          bannerName: getMessageCode(bannerCount),
          slideNumber,
        });
      }
      if (slidesType === SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER) {
        track('dismiss-upsell-pop-up', {
          pageName: selector.name ?? pageName,
          bannerName: getCSRLSMessageCode(bannerCount),
        });
      }
    },
    [slidesType, pageName, track]
  );

  const modalSize = useMemo(() => {
    let size: 'small' | 'default' | 'big' = 'default';
    if (slidesType === SlidesByType.CONSUMER_LOST_SHOPPER) {
      size = 'big';
    }
    return size;
  }, [slidesType]);

  return (
    <OutsideClickHandler onOutsideClick={onOutsideClick}>
      <UpsellContext.Provider value={contextValue}>
        {showBanner && (
          <UpsellMessage
            onClose={onUpsellMessageClose}
            onShowMore={onShowMore}
            pageName={pageName}
            bannerIndex={bannerCount}
          />
        )}
        <Notification />
        <UpsellModal slides={slides} size={modalSize} onCloseTrack={onCloseTrack} />
      </UpsellContext.Provider>
    </OutsideClickHandler>
  );
}
